import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import { Box, FlexBox, Typography, Logo, Grid, MuiBox } from '@kis-technologies/kis-ui';
import { TextSlider } from 'components/TextSlider';
import { useActions } from 'hooks/useActions';
import { selectors as configSelectors, Configuration, LanguageField } from 'services/configuration';
import { selectors as hardwareSelectors, actions as hardwareActions } from 'services/hardware';
import { selectors as kioskSelectors, actions as kioskActions } from 'services/kiosk';
import { actions as mediaActions, selectors as mediaSelectors } from 'services/media';
import { getConfiguredOrTranslationValues, readTranslationFiles } from 'utils/helpers';
import { isCurrentTimeInRange } from '../utils';
import { useStyles, OfflineViewImage } from './styled';

const OfflineView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { getMedia, paymentPing, logToLocalServer, updateKiosk, setStatusActor } = useActions({
    getMedia: mediaActions.getMedia,
    paymentPing: hardwareActions.paymentPing,
    logToLocalServer: hardwareActions.logToLocalServer,
    updateKiosk: kioskActions.updateKiosk,
    setStatusActor: kioskActions.setStatusActor
  });
  const lowStockThresholdReached = useSelector(kioskSelectors.selectLowStockThresholdReached);
  const kiosk = useSelector(kioskSelectors.selectKiosk);
  const kioskPayment = useSelector(kioskSelectors.selectPaymentHardware);
  const statusActor = useSelector(kioskSelectors.selectStatusActor);
  const anyHardwareErrors = useSelector(hardwareSelectors.selectAnyHardwareErrors);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const inactiveImage = useSelector(mediaSelectors.selectInactiveImage) || [];
  const backgroundImage = useSelector(mediaSelectors.selectBackgroundImage) || [];
  const logoImage = useSelector(mediaSelectors.selectLogoImage) || [];

  const flow = get(config, 'configuration.flowTemplate', '');
  const languageSettings: LanguageField[] = get(config, 'configuration.languageSettings', []);
  const palette = get(config, 'configuration.globalSettings.palette');
  const fonts = get(config, 'configuration.globalSettings.fonts');
  const fontFamily = get(fonts, 'primary', '');
  const support = get(config, `ticketing.supportName.${i18n.language}`) || t('commonText.support');
  const inactivePageHeader = get(config, `configuration.inactivePage.messages.header.${i18n.language}`);
  const inactivePageSubheader = get(config, `configuration.inactivePage.messages.subheader.${i18n.language}`);
  const languageTransitionTime = get(config, `configuration.inactivePage.languageTransitionTime`);
  const clientId = get(kiosk, 'venue.clientId');
  const disablePingRanges: string[] = get(kiosk, 'configuration.disableHardwarePing', []);
  const bgImageName = get(backgroundImage[0], 's3Name') || 'default.png';

  const [configuredLangTranslations, setConfiguredLangTranslations] = useState();
  const classes = useStyles({ flow, fontFamily })();
  const isOfflineBySchedule =
    (!kiosk || !kiosk.unscheduledUntil || new Date(kiosk.unscheduledUntil) < new Date()) &&
    !lowStockThresholdReached &&
    !anyHardwareErrors;
  const imageBaseUrl = `${process.env.REACT_APP_S3_ASSETS}/${clientId}`;
  const previewUrl = `${imageBaseUrl}/inactive_images/`;
  const backgroundUrl = `${imageBaseUrl}/background_images/${bgImageName}`;

  useEffect(() => {
    // Ping hardware every 30 seconds for any errors
    let interval: ReturnType<typeof setInterval>;
    if (kioskPayment && statusActor === 'paymentDeviceError') {
      logToLocalServer('SELF_HEAL: Starting self heal for payment device error');
      interval = setInterval(() => {
        if (isValidPingTimeRange()) {
          paymentPing();
        }
      }, 30000);
    }

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Get translations for the text slider
    const getData = async () => {
      const languages = languageSettings.map((lang) => lang.langCode);
      const translationStrings: any = await readTranslationFiles(languages);
      for (const lang of languages) {
        i18n.addResourceBundle(lang, 'translation', translationStrings[lang]);
      }
      setConfiguredLangTranslations(translationStrings);
    };

    if (languageSettings.length && !configuredLangTranslations) {
      getData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuredLangTranslations]);

  useEffect(() => {
    if (!anyHardwareErrors && statusActor === 'paymentDeviceError') {
      const payload = {
        status: 'Active'
      };

      updateKiosk(payload);
      setStatusActor('selfHeal');
      logToLocalServer('SELF_HEAL: Completed self heal and set kiosk to active state');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyHardwareErrors]);

  useEffect(() => {
    getMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const isValidPingTimeRange = () => {
    let isValid = true;
    if (disablePingRanges.length) {
      isValid = !isCurrentTimeInRange(disablePingRanges);
    }

    return isValid;
  };

  const backgroundStyle = () => {
    if (flow === 'fair') {
      return {
        backgroundImage: `url(${backgroundUrl})`,
        height: '100%'
      };
    }
    return {};
  };

  const offlineHeader = () => {
    if (flow === 'fair') {
      return (
        <Typography fontSize={125} sx={{ color: palette?.primaryText, fontFamily: fonts?.secondary }} align="center">
          {inactivePageHeader ? inactivePageHeader : 'This Kiosk is Inactive.'}
        </Typography>
      );
    }
    return (
      <Typography className={classes.offlineHeaderText}>
        {inactivePageHeader ? inactivePageHeader : t('offline.kioskOfflineHeader')}
      </Typography>
    );
  };
  const offlineMessage = () => {
    if (flow === 'fair') {
      return (
        <Typography className={classes.offlineText}>
          TO PURCHASE COUPONS PLEASE USE ANOTHER KIOSK OR COUPON BOOTH.
        </Typography>
      );
    }
    return inactivePageSubheader ? (
      <Typography className={classes.offlineText}>{inactivePageSubheader}</Typography>
    ) : (
      <Typography className={classes.offlineText}>
        {lowStockThresholdReached && `${t('offline.lowTicketsMessage')} `}
        {isOfflineBySchedule && `${t('offline.scheduledDowntimeMessage')} `}
        {t('offline.kioskOfflineSubheader', { support })}
      </Typography>
    );
  };

  return (
    <FlexBox noWrap sx={backgroundStyle()} direction="column" align="center" justify="center" padding="0 5">
      {flow === 'fair' && (
        <FlexBox height="12.5%" direction="column" fullWidth align="center" justify="center">
          {logoImage && logoImage.length > 0 ? (
            <img alt="url" src={`${imageBaseUrl}/logo_images/${logoImage[0].s3Name}`} style={{ height: 97 }} />
          ) : (
            <Logo />
          )}
        </FlexBox>
      )}
      <Grid className={classes.offlineContainer}>
        {flow !== 'fair' && (
          <>
            {inactiveImage && inactiveImage.length > 0 ? (
              <>
                {inactivePageHeader || inactivePageSubheader ? (
                  <>
                    <Grid item lg={12} xl={6} className={classes.offlineImageBlock}>
                      <FlexBox bg="x-light-gray">
                        <OfflineViewImage
                          alt="offline-view-image"
                          src={`${previewUrl}${inactiveImage[0].s3Name}`}
                          className={classes.offlineImage}
                        />
                      </FlexBox>
                    </Grid>
                    <Grid item lg={12} xl={6} className={classes.offlineTextBlock}>
                      <FlexBox justify="center" direction="column" padding="5 10">
                        {languageSettings.length && languageTransitionTime ? (
                          <TextSlider
                            height={'auto'}
                            width={'600px'}
                            items={getConfiguredOrTranslationValues(
                              languageSettings,
                              i18n,
                              'offline.kioskOfflineHeader',
                              'offline.kioskOfflineSubheader',
                              config,
                              'configuration.inactivePage.messages.header',
                              'configuration.inactivePage.messages.subheader'
                            )}
                            isInactive
                            fontFamily={fontFamily}
                            transitionTime={languageTransitionTime}
                          />
                        ) : (
                          <>
                            {offlineHeader()}
                            <Box height="30px" flexOverride="none" />
                            <Box>{offlineMessage()}</Box>
                          </>
                        )}
                      </FlexBox>
                    </Grid>
                  </>
                ) : (
                  <>
                    <FlexBox fullWidth fullHeight align="center" justify="center" direction="column" padding="0">
                      <MuiBox className={classes.offlineImageOnly}>
                        <FlexBox bg="x-light-gray" fullWidth height="100%">
                          <OfflineViewImage
                            alt="offline-view-image"
                            src={`${previewUrl}${inactiveImage[0].s3Name}`}
                            className={classes.offlineImage}
                          />
                        </FlexBox>
                      </MuiBox>
                    </FlexBox>
                  </>
                )}
              </>
            ) : (
              <FlexBox width="600px" align="center" justify="center" direction="column">
                {languageSettings.length && languageTransitionTime ? (
                  <TextSlider
                    height={'auto'}
                    width={'600px'}
                    items={getConfiguredOrTranslationValues(
                      languageSettings,
                      i18n,
                      'offline.kioskOfflineHeader',
                      'offline.kioskOfflineSubheader',
                      config,
                      'configuration.inactivePage.messages.header',
                      'configuration.inactivePage.messages.subheader'
                    )}
                    isInactive
                    fontFamily={fontFamily}
                    transitionTime={languageTransitionTime}
                  />
                ) : (
                  <>
                    {offlineHeader()}
                    <MuiBox className={classes.offlineTextDivider} />
                    <Box>{offlineMessage()}</Box>
                  </>
                )}
              </FlexBox>
            )}
          </>
        )}
        {flow === 'fair' && (
          <FlexBox width="600px" align="center" justify="center" direction="column">
            {offlineHeader()}
            <Box height="30px" flexOverride="none" />
            <Box>{offlineMessage()}</Box>
          </FlexBox>
        )}
      </Grid>
    </FlexBox>
  );
};

export default OfflineView;
